import firebase from "firebase/app";
import "firebase/messaging";
import { FIREBASE_CONFIG, VAPID_KEY } from "@/common/constants/notifications";
import {
  ENDPOINT_REGISTER_DEVICE,
  ENDPOINT_UNREGISTER_DEVICE,
} from "@/common/constants/api";
import { httpPost, httpDelete } from "@/services/api.service";
import lodashGet from "lodash/get";
import { getFormatted } from "@/common/date.utilities";
import {
  SET_FIREBASE_ENABLED,
  SET_ALERT_PUSH_TIMESTAMP,
} from "@/store/mutations.type";
import store from "@/store";
import { MODULE_PREFIX_HOME, MODULE_PREFIX_ALERTS_CONTENT } from "@/store";
import {
  hasFirebaseAccess,
  hasWatchOnlyAccess,
  hasSearchOnlyAccess,
} from "@/services/user/user.service";

export { _initialize as initialize };
export { _defaultSetUp as defaultSetUp };
export { _deleteToken as deleteToken };
export { _setBackgroundNotificationCallback as setBackgroundNotificationCallback };

let _messaging = null;

/**
 *
 * @param params.userProfile
 * @param params.options
 * @private
 */
async function _defaultSetUp(options) {
  if (!hasFirebaseAccess()) {
    updateStore(false);
    return false;
  }

  await _deleteToken();

  if (!hasWatchOnlyAccess() && !hasSearchOnlyAccess()) {
    const hasPermission = await getCheckNotificationPermission();
    if (hasPermission) {
      await _getToken();
    } else {
      updateStore(false);
      return false;
    }
    if (options.hasOwnProperty("notificationCallback")) {
      _setBackgroundNotificationCallback(options.notificationCallback);
    }
    updateStore(true);
  }
  return true;
}

function updateStore(enabled) {
  store.commit(MODULE_PREFIX_HOME + SET_FIREBASE_ENABLED, enabled);
}

function _initialize(registration) {
  firebase.initializeApp(FIREBASE_CONFIG);
  setupFbMessaging();
  _messaging.usePublicVapidKey(VAPID_KEY);
  _messaging.onTokenRefresh(_getToken);
  //if for some reason BackgroundMessageHandler starts acknowledging its not in the background put this back
  // _messaging.onMessage(_messageHandler);
  if (registration) {
    _messaging.useServiceWorker(registration);
  }
  return firebase;
}

async function _deleteToken() {
  let currentToken;
  try {
    currentToken = await getMessagingToken();
  } catch (err) {
    if (err.code === "messaging/permission-blocked") {
      return;
    }
    console.error(err);
  }

  // unregister us
  try {
    await httpDelete({
      url: ENDPOINT_UNREGISTER_DEVICE,
      data: {
        token: currentToken,
        platform: "web-ui",
      },
    });
  } catch (err) {
    console.log("failed to unregister Seerist notifications");
    console.log(err);
  }

  // unregister them
  try {
    await _messaging.deleteToken(currentToken);
  } catch (err) {
    console.log("failed to unregister FCM");
    console.log(err);
  }
}

async function _getToken() {
  try {
    const refreshedToken = await getMessagingToken();
    await _tokenEventHandler({ token: refreshedToken });
  } catch (error) {
    console.log("_getTokenError");
    console.log(error);
    _tokenEventHandler({
      message: "Unable to retrieve refreshed token",
      error: error,
    });
  }
}

async function _tokenEventHandler(tokenInfo) {
  if (tokenInfo.token) {
    await httpPost({
      url: ENDPOINT_REGISTER_DEVICE,
      data: {
        token: tokenInfo.token,
        platform: "web-ui",
      },
    });
  } else {
    console.log(
      "No Instance ID token available. Request permission to generate one."
    );
  }
}

function _setBackgroundNotificationCallback(callback) {
  if (navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener("message", function (e) {
      let data;
      if (lodashGet(e, "data.firebaseMessaging.payload.data")) {
        //When the notification hit while the browser has focus
        data = lodashGet(e, "data.firebaseMessaging.payload.data");
      } else if (lodashGet(e, "data")) {
        //When the notification hit while the browser does not have focus
        let parsed = JSON.parse(e.data);
        if (parsed.data) {
          data = parsed.data;
        }
      }

      if (data) {
        store.commit(
          MODULE_PREFIX_ALERTS_CONTENT + SET_ALERT_PUSH_TIMESTAMP,
          new Date()
        );
        callback(_formatAlert(data));
      }
    });
  }
}

function _formatAlert(alert) {
  let res = {};

  if (alert.alertId) {
    res.alertId = alert.alertId;
  }
  if (alert.triggeredId) {
    res.triggeredId = alert.triggeredId;
  }

  if (alert.timestamp) {
    let timestamp =
      typeof alert.timestamp === "string"
        ? parseInt(alert.timestamp)
        : alert.timestamp;
    let date = new Date(timestamp);
    let formatted = getFormatted(date);
    res.timestamp_ms = timestamp;
    res.timestamp_ms_utc = timestamp;
    res.startTime = date.toISOString();
    res.formatted = formatted;
  }

  if (alert.message) {
    res.message = alert.message;
  }
  return res;
}

async function getCheckNotificationPermission() {
  const permissionRequestResults = await Notification.requestPermission();
  return permissionRequestResults === "granted";
}

async function getMessagingToken() {
  setupFbMessaging();
  return await _messaging.getToken();
}

async function setupFbMessaging() {
  if (!_messaging) {
    _messaging = firebase.messaging();
  }
  if (!_messaging) {
    throw "_messaging not initialized";
  }
  return _messaging;
}
